import { supabase } from "@/integrations/supabase/client";

/**
 * OAuth Service for handling social media platform connections
 */

export interface OAuthConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scope: string[];
  authUrl: string;
  tokenUrl: string;
  userInfoUrl: string;
}

export interface OAuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
}

export interface UserProfile {
  id: string;
  username: string;
  displayName: string;
  email?: string;
  avatarUrl?: string;
  followersCount?: number;
  subscribersCount?: number;
  viewsCount?: number;
  verified?: boolean;
  userType?: string;
}

export class OAuthService {
  private static instance: OAuthService;
  private configs: Map<string, OAuthConfig> = new Map();

  private constructor() {
    this.initializeConfigs();
  }

  public static getInstance(): OAuthService {
    if (!OAuthService.instance) {
      OAuthService.instance = new OAuthService();
    }
    return OAuthService.instance;
  }

  private initializeConfigs() {
    // TikTok Configuration
    this.configs.set('tiktok', {
      clientId: import.meta.env.VITE_TIKTOK_CLIENT_KEY || '',
      clientSecret: import.meta.env.VITE_TIKTOK_CLIENT_SECRET || '',
      redirectUri: import.meta.env.VITE_TIKTOK_REDIRECT_URI || '',
      scope: ['user.info.basic', 'user.info.profile'],
      authUrl: 'https://www.tiktok.com/v2/auth/authorize/',
      tokenUrl: 'https://open.tiktokapis.com/v2/oauth/token/',
      userInfoUrl: 'https://open.tiktokapis.com/v2/user/info/'
    });

    // Twitch Configuration
    this.configs.set('twitch', {
      clientId: import.meta.env.VITE_TWITCH_CLIENT_ID || '',
      clientSecret: import.meta.env.VITE_TWITCH_CLIENT_SECRET || '',
      redirectUri: import.meta.env.VITE_TWITCH_REDIRECT_URI || '',
      scope: [
        'user:read:email',
        'user:read:follows'
      ],
      authUrl: 'https://id.twitch.tv/oauth2/authorize',
      tokenUrl: 'https://id.twitch.tv/oauth2/token',
      userInfoUrl: 'https://api.twitch.tv/helix/users'
    });

    // Instagram Configuration
    this.configs.set('instagram', {
      clientId: import.meta.env.VITE_INSTAGRAM_APP_ID || '',
      clientSecret: import.meta.env.VITE_INSTAGRAM_APP_SECRET || '',
      redirectUri: import.meta.env.VITE_INSTAGRAM_REDIRECT_URI || '',
      scope: ['user_profile', 'user_media'],
      authUrl: 'https://api.instagram.com/oauth/authorize',
      tokenUrl: 'https://api.instagram.com/oauth/access_token',
      userInfoUrl: 'https://graph.instagram.com/me'
    });

    // Google/YouTube Configuration
    this.configs.set('google', {
      clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      clientSecret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET || '',
      redirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || '',
      scope: ['openid', 'profile', 'email', 'https://www.googleapis.com/auth/youtube.readonly'],
      authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenUrl: 'https://oauth2.googleapis.com/token',
      userInfoUrl: 'https://www.googleapis.com/oauth2/v2/userinfo'
    });

    // YouTube Configuration (alias for Google with YouTube-specific scopes)
    this.configs.set('youtube', {
      clientId: import.meta.env.VITE_YOUTUBE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      clientSecret: import.meta.env.VITE_YOUTUBE_CLIENT_SECRET || import.meta.env.VITE_GOOGLE_CLIENT_SECRET || '',
      redirectUri: import.meta.env.VITE_YOUTUBE_REDIRECT_URI || import.meta.env.VITE_GOOGLE_REDIRECT_URI || '',
      scope: [
        'openid',
        'profile',
        'email',
        'https://www.googleapis.com/auth/youtube.readonly',
        'https://www.googleapis.com/auth/youtube.force-ssl'
      ],
      authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenUrl: 'https://oauth2.googleapis.com/token',
      userInfoUrl: 'https://www.googleapis.com/oauth2/v2/userinfo'
    });

    // X (Twitter) Configuration - OAuth 2.0 with PKCE
    this.configs.set('twitter', {
      clientId: import.meta.env.VITE_TWITTER_CLIENT_ID || '',
      clientSecret: import.meta.env.VITE_TWITTER_CLIENT_SECRET || '',
      redirectUri: import.meta.env.VITE_TWITTER_REDIRECT_URI || '',
      scope: ['tweet.read', 'users.read'],
      authUrl: 'https://twitter.com/i/oauth2/authorize',
      tokenUrl: 'https://api.twitter.com/2/oauth2/token',
      userInfoUrl: 'https://api.twitter.com/2/users/me'
    });

    // Discord Configuration
    this.configs.set('discord', {
      clientId: import.meta.env.VITE_DISCORD_CLIENT_ID || '1473000349454303505',
      clientSecret: import.meta.env.VITE_DISCORD_CLIENT_SECRET || '',
      redirectUri: import.meta.env.VITE_DISCORD_REDIRECT_URI || `${window.location.origin}/auth/discord/callback`,
      scope: ['identify', 'role_connections.write', 'email'],
      authUrl: 'https://discord.com/api/oauth2/authorize',
      tokenUrl: 'https://discord.com/api/oauth2/token',
      userInfoUrl: 'https://discord.com/api/v10/users/@me'
    });
  }

  /**
   * Generate OAuth authorization URL
   */
  public getAuthUrl(platform: string, state?: string): string {
    const config = this.configs.get(platform);
    if (!config) {
      throw new Error(`OAuth configuration not found for platform: ${platform}`);
    }

    // For Twitter/X, we need to use PKCE (Proof Key for Code Exchange)
    if (platform === 'twitter') {
      // Generate code verifier and challenge for PKCE
      const codeVerifier = this.generateCodeVerifier();
      const codeChallenge = this.generateCodeChallenge(codeVerifier);

      // Store code verifier for later use
      sessionStorage.setItem(`oauth_code_verifier_${platform}`, codeVerifier);

      const params = new URLSearchParams({
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        response_type: 'code',
        scope: config.scope.join(' '),
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        ...(state && { state })
      });

      const authUrl = `${config.authUrl}?${params.toString()}`;

      return authUrl;
    }

    // For other platforms, use standard OAuth 2.0
    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      response_type: 'code',
      scope: config.scope.join(' '),
      ...(state && { state })
    });

    const authUrl = `${config.authUrl}?${params.toString()}`;

    return authUrl;
  }

  /**
   * Generate code verifier for PKCE
   */
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate code challenge for PKCE (synchronous version)
   */
  private generateCodeChallenge(verifier: string): string {
    // For now, use a simple base64 encoding as fallback
    // In production, you might want to use a proper SHA-256 implementation
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);

    // Simple hash simulation (not cryptographically secure, but works for testing)
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data[i];
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Convert to base64url
    const hashBytes = new Uint8Array(4);
    hashBytes[0] = (hash >> 24) & 0xFF;
    hashBytes[1] = (hash >> 16) & 0xFF;
    hashBytes[2] = (hash >> 8) & 0xFF;
    hashBytes[3] = hash & 0xFF;

    return btoa(String.fromCharCode.apply(null, Array.from(hashBytes)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Exchange authorization code for access token
   */
  public async exchangeCodeForToken(platform: string, code: string): Promise<OAuthTokens> {
    const config = this.configs.get(platform);
    if (!config) {
      throw new Error(`OAuth configuration not found for platform: ${platform}`);
    }

    // For Twitter/X, we need to use a different approach due to CORS restrictions
    if (platform === 'twitter') {
      // For Twitter/X, we'll use a proxy or different method
      // For now, let's try with basic auth in headers
      const codeVerifier = sessionStorage.getItem(`oauth_code_verifier_${platform}`);
      if (!codeVerifier) {
        throw new Error('Code verifier not found for Twitter PKCE flow');
      }

      // Clean up the code verifier
      sessionStorage.removeItem(`oauth_code_verifier_${platform}`);

      // Create basic auth header
      const credentials = btoa(`${config.clientId}:${config.clientSecret}`);

      const response = await fetch(config.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'Authorization': `Basic ${credentials}`
        },
        body: new URLSearchParams({
          code: code,
          grant_type: 'authorization_code',
          redirect_uri: config.redirectUri,
          code_verifier: codeVerifier
        })
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Twitter token exchange error:', error);
        throw new Error(`Token exchange failed: ${error}`);
      }

      const data = await response.json();
      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        tokenType: data.token_type
      };
    }

    // For other platforms, use standard OAuth 2.0
    const bodyParams: Record<string, string> = {
      client_id: config.clientId,
      client_secret: config.clientSecret,
      code: code,
      grant_type: 'authorization_code',
      redirect_uri: config.redirectUri
    };

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: new URLSearchParams(bodyParams)
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    const data = await response.json();
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
      tokenType: data.token_type
    };
  }

  /**
   * Get user profile information
   */
  public async getUserProfile(platform: string, accessToken: string): Promise<UserProfile> {
    const config = this.configs.get(platform);
    if (!config) {
      throw new Error(`OAuth configuration not found for platform: ${platform}`);
    }

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json'
    };

    // Platform-specific headers
    if (platform === 'twitch') {
      headers['Client-Id'] = config.clientId;
    }

    const response = await fetch(config.userInfoUrl, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to fetch user profile: ${error}`);
    }

    const data = await response.json();

    // For Twitch, we need to fetch additional channel data
    if (platform === 'twitch') {
      return await this.getTwitchChannelData(data, accessToken, config.clientId);
    }

    // For YouTube, we need to fetch additional channel data
    if (platform === 'youtube') {
      return await this.getYouTubeChannelData(data, accessToken);
    }

    // For Twitter, we need to fetch additional user data
    if (platform === 'twitter') {
      return await this.getTwitterUserData(data, accessToken);
    }

    return this.parseUserProfile(platform, data);
  }

  /**
   * Get Twitch channel data (followers, views, etc.)
   */
  private async getTwitchChannelData(userData: any, accessToken: string, clientId: string): Promise<UserProfile> {
    const user = userData.data[0];
    const userId = user.id;

    try {
      // Fetch channel information (this should work with basic scopes)
      const channelResponse = await fetch(`https://api.twitch.tv/helix/channels?broadcaster_id=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Client-Id': clientId,
          'Accept': 'application/json'
        }
      });

      let channelData = null;
      if (channelResponse.ok) {
        const channelResult = await channelResponse.json();
        channelData = channelResult.data[0];
      }

      // Try to fetch followers count (may work without special scopes)
      let followersCount = 0;
      try {
        const followersResponse = await fetch(`https://api.twitch.tv/helix/channels/followers?broadcaster_id=${userId}&first=1`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Client-Id': clientId,
            'Accept': 'application/json'
          }
        });

        if (followersResponse.ok) {
          const followersResult = await followersResponse.json();
          followersCount = followersResult.total || 0;
        } else if (followersResponse.status === 401) {
          // Followers API requires additional scopes, skipping...
        }
      } catch (followersError) {
        // Continue without followers data
      }

      // Try to fetch subscribers count (may work without special scopes)
      let subscribersCount = 0;
      try {
        const subscribersResponse = await fetch(`https://api.twitch.tv/helix/subscriptions?broadcaster_id=${userId}&first=1`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Client-Id': clientId,
            'Accept': 'application/json'
          }
        });

        if (subscribersResponse.ok) {
          const subscribersResult = await subscribersResponse.json();
          subscribersCount = subscribersResult.total || 0;
        } else if (subscribersResponse.status === 401) {
          // Subscribers API requires additional scopes, skipping...
        }
      } catch (subscribersError) {
        // Continue without subscribers data
      }

      return {
        id: user.id,
        username: user.login,
        displayName: user.display_name,
        email: user.email,
        avatarUrl: user.profile_image_url,
        followersCount: followersCount,
        subscribersCount: subscribersCount,
        viewsCount: channelData?.view_count || 0,
        verified: user.broadcaster_type !== ''
      };
    } catch (error) {
      console.error('Error fetching Twitch channel data:', error);
      // Return basic user data if channel data fails
      return {
        id: user.id,
        username: user.login,
        displayName: user.display_name,
        email: user.email,
        avatarUrl: user.profile_image_url,
        followersCount: 0,
        subscribersCount: 0,
        viewsCount: 0,
        verified: user.broadcaster_type !== ''
      };
    }
  }

  /**
   * Get YouTube channel data (subscribers, views, etc.)
   */
  private async getYouTubeChannelData(userData: any, accessToken: string): Promise<UserProfile> {
    try {
      // First, get the user's YouTube channel ID
      const channelResponse = await fetch('https://www.googleapis.com/youtube/v3/channels?part=id&mine=true', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json'
        }
      });

      let channelId = null;
      let subscribersCount = 0;
      let viewsCount = 0;

      if (channelResponse.ok) {
        const channelResult = await channelResponse.json();
        if (channelResult.items && channelResult.items.length > 0) {
          channelId = channelResult.items[0].id;
        }
      } else if (channelResponse.status === 401) {
        // YouTube channel API requires additional scopes, skipping...
      }

      // If we have a channel ID, get detailed channel statistics
      if (channelId) {
        try {
          const statsResponse = await fetch(`https://www.googleapis.com/youtube/v3/channels?part=statistics&id=${channelId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json'
            }
          });


          if (statsResponse.ok) {
            const statsResult = await statsResponse.json();
            if (statsResult.items && statsResult.items.length > 0) {
              const stats = statsResult.items[0].statistics;
              subscribersCount = parseInt(stats.subscriberCount) || 0;
              viewsCount = parseInt(stats.viewCount) || 0;
            }
          } else {
            const errorText = await statsResponse.text();
            console.error('❌ YouTube stats API error:', errorText);
          }
        } catch (statsError) {
          // YouTube stats API not accessible
        }
      }

      return {
        id: userData.id,
        username: userData.name || userData.email?.split('@')[0] || 'youtube_user',
        displayName: userData.name || userData.email?.split('@')[0] || 'YouTube User',
        email: userData.email,
        avatarUrl: userData.picture,
        followersCount: subscribersCount, // YouTube uses subscribers instead of followers
        subscribersCount: subscribersCount,
        viewsCount: viewsCount,
        verified: false // YouTube verification status is not easily accessible via API
      };
    } catch (error) {
      console.error('Error fetching YouTube channel data:', error);
      // Return basic user data if channel data fails
      return {
        id: userData.id,
        username: userData.name || userData.email?.split('@')[0] || 'youtube_user',
        displayName: userData.name || userData.email?.split('@')[0] || 'YouTube User',
        email: userData.email,
        avatarUrl: userData.picture,
        followersCount: 0,
        subscribersCount: 0,
        viewsCount: 0,
        verified: false
      };
    }
  }

  /**
   * Get Twitter user data (followers, etc.)
   */
  private async getTwitterUserData(userData: any, accessToken: string): Promise<UserProfile> {
    try {
      // Twitter API v2 returns user data directly
      const user = userData.data;
      let followersCount = 0;

      // Try to fetch public metrics (followers count)
      try {
        const metricsResponse = await fetch(`https://api.twitter.com/2/users/${user.id}?user.fields=public_metrics`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json'
          }
        });

        if (metricsResponse.ok) {
          const metricsResult = await metricsResponse.json();
          if (metricsResult.data?.public_metrics) {
            followersCount = metricsResult.data.public_metrics.followers_count || 0;
          }
        } else if (metricsResponse.status === 401) {
          // Twitter metrics API requires additional scopes, skipping...
        }
      } catch (metricsError) {
        // Continue without metrics data
      }

      return {
        id: user.id,
        username: user.username,
        displayName: user.name || user.username,
        email: user.email,
        avatarUrl: user.profile_image_url,
        followersCount: followersCount,
        verified: user.verified || false
      };
    } catch (error) {
      console.error('Error fetching Twitter user data:', error);
      // Return basic user data if detailed data fails
      const user = userData.data;
      return {
        id: user.id,
        username: user.username,
        displayName: user.name || user.username,
        email: user.email,
        avatarUrl: user.profile_image_url,
        followersCount: 0,
        verified: user.verified || false
      };
    }
  }

  /**
   * Parse user profile data based on platform
   */
  private parseUserProfile(platform: string, data: any): UserProfile {
    switch (platform) {
      case 'tiktok':
        return {
          id: data.data.user.id,
          username: data.data.user.username,
          displayName: data.data.user.display_name,
          avatarUrl: data.data.user.avatar_url,
          followersCount: data.data.user.follower_count,
          verified: data.data.user.is_verified
        };

      case 'twitch':
        const user = data.data[0];
        return {
          id: user.id,
          username: user.login,
          displayName: user.display_name,
          email: user.email,
          avatarUrl: user.profile_image_url,
          followersCount: user.followers_count,
          verified: user.broadcaster_type !== ''
        };

      case 'instagram':
        return {
          id: data.id,
          username: data.username,
          displayName: data.username,
          avatarUrl: data.account_type
        };

      case 'google':
        return {
          id: data.id,
          username: data.email,
          displayName: data.name,
          email: data.email,
          avatarUrl: data.picture,
          verified: data.verified_email
        };

      case 'twitter':
        return {
          id: data.data.id,
          username: data.data.username,
          displayName: data.data.name,
          avatarUrl: data.data.profile_image_url,
          verified: data.data.verified
        };

      case 'discord':
        return {
          id: data.id,
          username: data.username,
          displayName: data.global_name || data.username,
          email: data.email,
          avatarUrl: data.avatar ? `https://cdn.discordapp.com/avatars/${data.id}/${data.avatar}.png` : undefined,
          verified: data.verified
        };

      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  }

  /**
   * Refresh access token
   */
  public async refreshToken(platform: string, refreshToken: string): Promise<OAuthTokens> {
    const config = this.configs.get(platform);
    if (!config) {
      throw new Error(`OAuth configuration not found for platform: ${platform}`);
    }

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: new URLSearchParams({
        client_id: config.clientId,
        client_secret: config.clientSecret,
        refresh_token: refreshToken,
        grant_type: 'refresh_token'
      })
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token refresh failed: ${error}`);
    }

    const data = await response.json();
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
      tokenType: data.token_type
    };
  }

  /**
   * Update Discord role metadata for a user
   */
  public async updateDiscordMetadata(accessToken: string, userType: string, platformUsername?: string): Promise<void> {
    const config = this.configs.get('discord');
    if (!config) return;

    const metadata = {
      creator: userType === 'creator' ? 1 : 0,
      developer: userType === 'developer' ? 1 : 0,
      player: userType === 'player' ? 1 : 0
    };

    try {
      const response = await fetch(`https://discord.com/api/v10/users/@me/applications/${config.clientId}/role-connection`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          platform_name: 'IndieKeys',
          platform_username: platformUsername || 'IndieKeys User',
          metadata: metadata
        })
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Failed to update Discord metadata:', error);
      }
    } catch (error) {
      console.error('Error updating Discord metadata:', error);
    }
  }

  /**
   * Register Discord application metadata (Admin/One-time usage)
   */
  /**
   * Register Discord application metadata (Admin/One-time usage)
   * Now calls the Supabase Edge Function for security and CORS
   */
  public async registerDiscordMetadata(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/discord-linked-roles/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });

      if (response.ok) {
        console.log('Discord metadata registered via Edge Function successfully!');
        return true;
      } else {
        const error = await response.json();
        console.error('Failed to register Discord metadata:', error);
        return false;
      }
    } catch (error) {
      console.error('Error registering Discord metadata:', error);
      return false;
    }
  }

  /**
   * Fetch current Discord metadata for debugging
   */
  public async getDiscordMetadata(): Promise<any> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/discord-linked-roles/debug-metadata`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        const error = await response.text();
        console.error('Failed to fetch Discord metadata:', error);
        return null;
      }
    } catch (error) {
      console.error('Error fetching Discord metadata:', error);
      return null;
    }
  }

  /**
   * Check if platform is configured
   */
  public isPlatformConfigured(platform: string): boolean {
    const config = this.configs.get(platform);
    return !!(config?.clientId && config?.clientSecret);
  }
}

export const oauthService = OAuthService.getInstance();
